<template>
  <main>
    <div class="container-fluid">
      <!-- Header with background image -->
      <div
        class="page-header min-height-300"
        :style="{
          backgroundImage: `url('/img/bg-profile.df0b9ab6.jpg')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          marginRight: '-24px',
          marginLeft: '-24px'
        }"
      >
        <span class="mask bg-gradient-success opacity-6"></span>
      </div>

      <!-- Profile Card -->
      <div class="card shadow-lg mt-n6">
        <div class="card-body p-3">
          <div class="row gx-4">
            <!-- Avatar -->
            <div class="col-auto">
              <div class="avatar avatar-xl position-relative">
                <img
                  v-if="avatarSrc"
                  :src="avatarSrc"
                  alt="Profile Avatar"
                  class="shadow-sm w-100 border-radius-lg rounded-circle"
                />
                <div v-else class="avatar-placeholder rounded-circle">
                  <span>{{ userInitials }}</span>
                </div>
              </div>
            </div>
            
            <!-- User Details -->
            <div class="col-auto my-auto">
              <div class="h-100">
                <h5 class="mb-1">{{ displayName }}</h5>
                <p class="mb-0 font-weight-bold text-sm">{{ userData.email || t('profile.loading') }}</p>
              </div>
            </div>
            
            <!-- Edit Profile Button (for mobile view) -->
            <div class="col-lg-4 col-md-6 my-sm-auto ms-sm-auto me-sm-0 mt-3 d-md-none">
              <div class="d-flex justify-content-end">
                <button 
                  @click="toggleEditMode" 
                  class="btn btn-sm" 
                  :class="isEditMode ? 'btn-secondary' : 'btn-success'"
                >
                  <i class="fas" :class="isEditMode ? 'fa-times' : 'fa-pencil-alt'"></i>
                  {{ isEditMode ? t('profile.cancel') : t('profile.edit_profile') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Content -->
    <div class="py-4 container-fluid">
      <div class="row">
        <!-- Main Profile Information -->
        <div class="col-md-8 mb-4">
          <div class="card">
            <!-- Card Header -->
            <div class="card-header pb-0">
              <div class="d-flex align-items-center">
                <p class="mb-0">{{ isEditMode ? t('profile.edit_profile') : t('profile.profile_information') }}</p>
                <div class="ms-auto d-none d-md-block">
                  <button 
                    v-if="!isEditMode" 
                    @click="toggleEditMode" 
                    class="btn btn-sm" 
                    :style="{ backgroundColor: '#4A8C2A', color: 'white' }"
                  >
                    <i class="fas fa-pencil-alt me-2"></i>
                    {{ t('profile.edit_profile') }}
                  </button>
                  <button 
                    v-if="isEditMode" 
                    @click="saveChanges" 
                    class="btn btn-sm" 
                    :style="{ backgroundColor: '#4A8C2A', color: 'white' }"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-save me-2"></i>
                    {{ isLoading ? t('profile.saving') : t('profile.save_changes') }}
                  </button>
                  <button 
                    v-if="isEditMode" 
                    @click="toggleEditMode" 
                    class="btn btn-sm btn-secondary ms-2"
                  >
                    <i class="fas fa-times me-2"></i>
                    {{ t('profile.cancel') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="card-body">
              <!-- User Information Section -->
              <p class="text-uppercase text-sm">{{ t('profile.user_information') }}</p>
              <div class="row">
                <!-- Email (Non-editable) -->
                <div class="col-md-12 mb-3">
                  <label for="email" class="form-control-label">{{ t('profile.email_address') }}</label>
                  <input 
                    type="email" 
                    id="email" 
                    class="form-control" 
                    :value="userData.email" 
                    disabled 
                  />
                </div>

                <!-- First Name -->
                <div class="col-md-6 mb-3">
                  <label for="firstName" class="form-control-label">{{ t('profile.first_name') }}</label>
                  <input 
                    type="text" 
                    id="firstName" 
                    class="form-control" 
                    v-model="formData.firstName" 
                    :disabled="!isEditMode" 
                    :class="{ 'is-invalid': validationErrors.firstName }"
                  />
                  <div v-if="validationErrors.firstName" class="invalid-feedback">
                    {{ validationErrors.firstName }}
                  </div>
                </div>

                <!-- Last Name -->
                <div class="col-md-6 mb-3">
                  <label for="lastName" class="form-control-label">{{ t('profile.last_name') }}</label>
                  <input 
                    type="text" 
                    id="lastName" 
                    class="form-control" 
                    v-model="formData.lastName" 
                    :disabled="!isEditMode"
                    :class="{ 'is-invalid': validationErrors.lastName }"
                  />
                  <div v-if="validationErrors.lastName" class="invalid-feedback">
                    {{ validationErrors.lastName }}
                  </div>
                </div>
              </div>

              <hr class="horizontal dark" />

              <!-- Contact Information Section -->
              <p class="text-uppercase text-sm">{{ t('profile.contact_information') }}</p>
              <div class="row">
                <!-- Address -->
                <div class="col-md-12 mb-3">
                  <label for="address" class="form-control-label">{{ t('profile.address') }}</label>
                  <input 
                    type="text" 
                    id="address" 
                    class="form-control" 
                    v-model="formData.address" 
                    :disabled="!isEditMode"
                    :class="{ 'is-invalid': validationErrors.address }"
                  />
                  <div v-if="validationErrors.address" class="invalid-feedback">
                    {{ validationErrors.address }}
                  </div>
                </div>

                <!-- City -->
                <div class="col-md-4 mb-3">
                  <label for="city" class="form-control-label">{{ t('profile.city') }}</label>
                  <input 
                    type="text" 
                    id="city" 
                    class="form-control" 
                    v-model="formData.city" 
                    :disabled="!isEditMode"
                    :class="{ 'is-invalid': validationErrors.city }"
                  />
                  <div v-if="validationErrors.city" class="invalid-feedback">
                    {{ validationErrors.city }}
                  </div>
                </div>

                <!-- Country -->
                <div class="col-md-4 mb-3">
                  <label for="country" class="form-control-label">{{ t('profile.country') }}</label>
                  <input 
                    type="text" 
                    id="country" 
                    class="form-control" 
                    v-model="formData.country" 
                    :disabled="!isEditMode"
                    :class="{ 'is-invalid': validationErrors.country }"
                  />
                  <div v-if="validationErrors.country" class="invalid-feedback">
                    {{ validationErrors.country }}
                  </div>
                </div>

                <!-- Postal Code -->
                <div class="col-md-4 mb-3">
                  <label for="postalCode" class="form-control-label">{{ t('profile.postal_code') }}</label>
                  <input 
                    type="text" 
                    id="postalCode" 
                    class="form-control" 
                    v-model="formData.postalCode" 
                    :disabled="!isEditMode"
                    :class="{ 'is-invalid': validationErrors.postalCode }"
                  />
                  <div v-if="validationErrors.postalCode" class="invalid-feedback">
                    {{ validationErrors.postalCode }}
                  </div>
                </div>
              </div>

              <!-- Mobile View Save Button -->
              <div class="row mt-4 d-md-none">
                <div class="col-12">
                  <button 
                    v-if="isEditMode" 
                    @click="saveChanges" 
                    class="btn btn-sm w-100" 
                    :style="{ backgroundColor: '#4A8C2A', color: 'white' }"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-save me-2"></i>
                    {{ isLoading ? t('profile.saving') : t('profile.save_changes') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Information Card (can be customized further) -->
        <div class="col-md-4">
          <div class="card mb-4">
            <div class="card-header pb-0">
              <h6>{{ t('profile.terrasense_account') }}</h6>
            </div>
            <div class="card-body p-3 p-sm-4">
              <p class="text-sm">
                {{ t('profile.welcome_message') }}
              </p>
              <div class="mt-4 mb-2">
                <i class="fas fa-leaf text-success me-2"></i>
                <span class="text-sm font-weight-bold">{{ t('profile.terrasense_green_technology') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useUserService } from '../services/user.service';
import { useI18n } from 'vue-i18n';
import ToastService from '../services/toast.service';

export default {
  setup() {
    // State variables
    const userData = ref({});
    const isEditMode = ref(false);
    const isLoading = ref(false);
    const validationErrors = ref({});

    // Initialize services
    const { getUserProfile, updateUserProfile } = useUserService();
    const { user } = useAuth0();
    const { t } = useI18n();

    // Form data
    const formData = reactive({
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      country: '',
      postalCode: ''
    });

    // Get user initials (used as fallback when no avatar is available)
    const userInitials = computed(() => {
      if (!user.value?.name) return '';
      return user.value.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    });

    // Avatar image source - prioritize Auth0 user image
    const avatarSrc = computed(() => {
      return user.value?.picture || userData.value?.picture || '';
    });

    // Fetch user data on component mount
    const fetchUserData = async () => {
      isLoading.value = true;
      try {
        const response = await getUserProfile();
        userData.value = response.data;
        
        // Set form data based on user profile
        formData.firstName = userData.value.firstName || '';
        formData.lastName = userData.value.lastName || '';
        
        // Set contact information if available
        const contactInfo = userData.value.contactInformation || {};
        formData.address = contactInfo.address || '';
        formData.city = contactInfo.city || '';
        formData.country = contactInfo.country || '';
        formData.postalCode = contactInfo.postalCode || '';
        
        isLoading.value = false;
      } catch (error) {
        showAlert(t('profile.errors.load_data'), 'error');
        isLoading.value = false;
      }
    };

    // Display name computed property
    const displayName = computed(() => {
      if (userData.value) {
        if (userData.value.firstName && userData.value.lastName) {
          return `${userData.value.firstName} ${userData.value.lastName}`;
        } else if (user.value?.name) {
          return user.value.name;
        } else if (userData.value.name) {
          return userData.value.name;
        }
      }
      return t('profile.loading');
    });

    // Toggle edit mode
    const toggleEditMode = () => {
      if (isEditMode.value) {
        // Reset form data if canceling edit
        resetFormData();
      }
      isEditMode.value = !isEditMode.value;
    };

    // Reset form data to original values
    const resetFormData = () => {
      formData.firstName = userData.value.firstName || '';
      formData.lastName = userData.value.lastName || '';
      
      const contactInfo = userData.value.contactInformation || {};
      formData.address = contactInfo.address || '';
      formData.city = contactInfo.city || '';
      formData.country = contactInfo.country || '';
      formData.postalCode = contactInfo.postalCode || '';
      
      validationErrors.value = {};
    };

    // Validate form data
    const validateForm = () => {
      const errors = {};
      
      if (!formData.firstName.trim()) {
        errors.firstName = t('profile.validation.first_name_required');
      }
      
      if (!formData.lastName.trim()) {
        errors.lastName = t('profile.validation.last_name_required');
      }
      
      // Additional validation can be added here
      
      validationErrors.value = errors;
      return Object.keys(errors).length === 0;
    };

    // Save changes
    const saveChanges = async () => {
      if (!validateForm()) {
        showAlert(t('profile.validation.fix_errors'), 'error');
        return;
      }
      
      isLoading.value = true;
      try {
        // Prepare data for API in the expected format
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          contactInformation: {
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postalCode: formData.postalCode
          }
        };
        
        const response = await updateUserProfile(updateData);
        
        if (response.success) {
          // Update the local user data with the response data
          userData.value = response.data;
          showAlert(t('profile.success.updated'), 'success');
          isEditMode.value = false;
        } else {
          showAlert(t('profile.errors.update_failed'), 'error');
        }
      } catch (error) {
        showAlert(t('profile.errors.update_error'), 'error');
      } finally {
        isLoading.value = false;
      }
    };

    // Show alert message using ToastService
    const showAlert = (message, type) => {
      if (type === 'success') {
        ToastService.success(message);
      } else {
        ToastService.error(message);
      }
    };

    // Clear alert (no longer needed with ToastService, but kept for compatibility)
    const clearAlert = () => {
      // No-op as ToastService handles clearing automatically
    };

    // Fetch user data on component mount
    onMounted(() => {
      fetchUserData();
      
      // Set up body classes for styling
      document.body.classList.add('profile-page');
      return () => {
        document.body.classList.remove('profile-page');
      };
    });

    return {
      userData,
      formData,
      isEditMode,
      isLoading,
      validationErrors,
      displayName,
      avatarSrc,
      userInitials,
      toggleEditMode,
      saveChanges,
      clearAlert,
      t
    };
  }
};
</script>

<style scoped>
/* TerraSense brand styling */
h1, h2, h3, h4, h5, h6 {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
}

body, p, div, span, input, button {
  font-family: 'Open Sans', sans-serif;
}

/* Profile styling */
.page-header {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  background-size: cover;
  background-position: 50%;
  border-radius: 0.75rem;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  font-size: 2rem;
  color: #6c757d;
  min-width: 74px;
  min-height: 74px;
}

/* TerraSense green color override */
.btn-success, .bg-gradient-success {
  background-color: #4A8C2A !important;
  border-color: #4A8C2A !important;
}

.text-success {
  color: #4A8C2A !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-header {
    margin-left: -5% !important;
    margin-right: -5% !important;
    min-height: 200px !important;
    border-radius: 0.5rem;
  }
  
  .card-body {
    padding: 1rem !important;
  }
  
  .avatar-xl {
    width: 60px !important;
    height: 60px !important;
  }
  
  .mt-n6 {
    margin-top: -3rem !important;
  }
}

@media (max-width: 576px) {
  .page-header {
    min-height: 180px !important;
  }
  
  .container-fluid {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
}
</style>
